import { useEffect, useRef, useState } from 'react'
import { ShieldCheck, ChevronDown, ChevronUp, X } from 'lucide-react'
import { submitConsentRefresh, type ConsentLevel, type PendingRecord } from './api'

interface Props {
  records: PendingRecord[]
  language: 'el' | 'en'
  onDone: () => void
}

interface I18n {
  pageTitle: string
  pageIntro: string
  childrenLabel: string
  sectionTitle: string
  readFullInfo: string
  hideFullInfo: string
  infoDocumentTitle: string
  infoDocumentHtml: string
  medicalConsentTitle: string
  personalConsentTitle: string
  fullConsent: string
  partialConsent: string
  noConsent: string
  noConsentPersonal: string
  explainPlaceholder: string
  explainPlaceholderPersonal: string
  medicalDataAcknowledged: string
  personalDataAcknowledged: string
  termsLabel: string
  termsTextPart1: string
  termsLink: string
  termsTextPart2: string
  termsModalTitle: string
  termsModalContent: string
  warningTitle: string
  warningText: string
  confirmSubmitTitle: string
  confirmSubmitMessage: string
  ok: string
  goBack: string
  proceed: string
  submit: string
  submitting: string
  close: string
}

const STRINGS: Record<'el' | 'en', I18n> = {
  el: {
    pageTitle: 'Επικαιροποίηση Συγκατάθεσης',
    pageIntro:
      'Έχουμε επικαιροποιήσει το έντυπο συγκατάθεσης για τα ιατρικά και προσωπικά δεδομένα. Παρακαλούμε επιβεβαιώστε εκ νέου τη συγκατάθεσή σας για να συνεχίσετε.',
    childrenLabel: 'Παιδιά που αφορά',
    sectionTitle: 'Συγκατάθεση για Ιατρικά και Προσωπικά Δεδομένα',
    readFullInfo: 'Διαβάστε το πλήρες έντυπο πληροφόρησης',
    hideFullInfo: 'Απόκρυψη πλήρους εντύπου',
    infoDocumentTitle: 'Έντυπο Ενημέρωσης για την Επεξεργασία Δεδομένων',
    infoDocumentHtml:
      '<p>Σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (ΓΚΠΔ / GDPR), ' +
      'το The Ranch επεξεργάζεται τα ιατρικά και προσωπικά δεδομένα του παιδιού σας ' +
      'αποκλειστικά για τους σκοπούς της ασφαλούς συμμετοχής του στις δράσεις της κατασκήνωσης.</p>' +
      '<p>Τα δεδομένα διατηρούνται για όσο χρόνο απαιτείται από τη νομοθεσία και ' +
      'μοιράζονται μόνο με το ιατρικό και εκπαιδευτικό προσωπικό που το χρειάζεται.</p>' +
      '<p>Έχετε δικαίωμα πρόσβασης, διόρθωσης, διαγραφής και περιορισμού της επεξεργασίας ' +
      'των δεδομένων σας. Για οποιοδήποτε αίτημα επικοινωνήστε στο info@ranch.gr.</p>',
    medicalConsentTitle: 'Συγκατάθεση για Ιατρικά Δεδομένα',
    personalConsentTitle: 'Συγκατάθεση για Προσωπικά Δεδομένα',
    fullConsent: 'Δίνω πλήρη συγκατάθεση για την επεξεργασία των δεδομένων.',
    partialConsent: 'Δίνω μερική συγκατάθεση (παρακαλώ διευκρινίστε παρακάτω).',
    noConsent: 'Δεν δίνω συγκατάθεση για τα ιατρικά δεδομένα.',
    noConsentPersonal: 'Δεν δίνω συγκατάθεση για τα προσωπικά δεδομένα.',
    explainPlaceholder: 'Παρακαλώ διευκρινίστε για ποια ιατρικά δεδομένα δεν δίνετε συγκατάθεση…',
    explainPlaceholderPersonal:
      'Παρακαλώ διευκρινίστε για ποια προσωπικά δεδομένα δεν δίνετε συγκατάθεση…',
    medicalDataAcknowledged:
      'Δηλώνω ότι έχω διαβάσει και κατανοήσει την ενημέρωση για την επεξεργασία των ιατρικών δεδομένων του παιδιού μου.',
    personalDataAcknowledged:
      'Δηλώνω ότι έχω διαβάσει και κατανοήσει την ενημέρωση για την επεξεργασία των προσωπικών δεδομένων του παιδιού μου.',
    termsLabel: 'Όροι και Προϋποθέσεις',
    termsTextPart1: 'Έχω διαβάσει και αποδέχομαι τους',
    termsLink: 'Όρους και Προϋποθέσεις',
    termsTextPart2: 'της πλατφόρμας The Ranch.',
    termsModalTitle: 'Όροι και Προϋποθέσεις',
    termsModalContent:
      'Η χρήση της πλατφόρμας The Ranch υπόκειται στους όρους χρήσης που έχουν δημοσιευτεί στην ιστοσελίδα μας. Με την αποδοχή τους επιβεβαιώνετε ότι έχετε διαβάσει, κατανοήσει και συμφωνείτε με τους όρους.',
    warningTitle: 'Προσοχή',
    warningText:
      'Η μερική ή μηδενική συγκατάθεση για τα ιατρικά δεδομένα ενδέχεται να επηρεάσει την ικανότητά μας να παρέχουμε ασφαλή φροντίδα στο παιδί σας. Παρακαλώ επικοινωνήστε μαζί μας πριν την έναρξη της δράσης.',
    confirmSubmitTitle: 'Επιβεβαίωση Υποβολής',
    confirmSubmitMessage: 'Είστε σίγουρος ότι θέλετε να συνεχίσετε με αυτή την επιλογή;',
    ok: 'Εντάξει',
    goBack: 'Επιστροφή',
    proceed: 'Συνέχεια',
    submit: 'Υποβολή Συγκατάθεσης',
    submitting: 'Υποβολή...',
    close: 'Κλείσιμο',
  },
  en: {
    pageTitle: 'Consent Refresh',
    pageIntro:
      "We have updated our medical and personal data consent form. Please re-confirm your consent to continue.",
    childrenLabel: 'Applies to',
    sectionTitle: 'Medical and Personal Data Consent',
    readFullInfo: 'Read the full information document',
    hideFullInfo: 'Hide full information',
    infoDocumentTitle: 'Information Document on Data Processing',
    infoDocumentHtml:
      "<p>Under the EU General Data Protection Regulation (GDPR), The Ranch processes your child's " +
      'medical and personal data solely for the purposes of their safe participation in camp activities.</p>' +
      '<p>Data is retained for as long as required by law and shared only with the medical and ' +
      'educational staff who need it.</p>' +
      '<p>You have the right to access, correct, delete, and restrict the processing of your data. ' +
      'For any request, contact info@ranch.gr.</p>',
    medicalConsentTitle: 'Medical Data Consent',
    personalConsentTitle: 'Personal Data Consent',
    fullConsent: 'I give full consent for data processing.',
    partialConsent: 'I give partial consent (please explain below).',
    noConsent: 'I do not consent to processing of medical data.',
    noConsentPersonal: 'I do not consent to processing of personal data.',
    explainPlaceholder: 'Please specify which medical data you do not consent to…',
    explainPlaceholderPersonal: 'Please specify which personal data you do not consent to…',
    medicalDataAcknowledged:
      "I have read and understood the information about the processing of my child's medical data.",
    personalDataAcknowledged:
      "I have read and understood the information about the processing of my child's personal data.",
    termsLabel: 'Terms and Conditions',
    termsTextPart1: 'I have read and accept the',
    termsLink: 'Terms and Conditions',
    termsTextPart2: 'of The Ranch platform.',
    termsModalTitle: 'Terms and Conditions',
    termsModalContent:
      "Use of The Ranch platform is governed by the terms published on our website. By accepting them you confirm that you have read, understood, and agree to the terms.",
    warningTitle: 'Warning',
    warningText:
      "Partial or no consent for medical data may affect our ability to provide safe care for your child. Please contact us before the start of the activity.",
    confirmSubmitTitle: 'Confirm Submission',
    confirmSubmitMessage: 'Are you sure you want to proceed with this selection?',
    ok: 'OK',
    goBack: 'Go back',
    proceed: 'Proceed',
    submit: 'Submit Consent',
    submitting: 'Submitting...',
    close: 'Close',
  },
}

export function ConsentRefreshScreen({ records, language, onDone }: Props) {
  const s = STRINGS[language]

  const [medAck, setMedAck] = useState(true)
  const [perAck, setPerAck] = useState(true)
  const [medLevel, setMedLevel] = useState<ConsentLevel>('full')
  const [perLevel, setPerLevel] = useState<ConsentLevel>('full')
  const [medExpl, setMedExpl] = useState('')
  const [perExpl, setPerExpl] = useState('')
  const [terms, setTerms] = useState(false)
  const [showGdprInfo, setShowGdprInfo] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showConsentWarningDialog, setShowConsentWarningDialog] = useState(false)
  const [showSubmitConfirmDialog, setShowSubmitConfirmDialog] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const termsModalRef = useRef<HTMLDivElement>(null)
  const consentWarningRef = useRef<HTMLDivElement>(null)
  const submitConfirmRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (showTermsModal && termsModalRef.current && !termsModalRef.current.contains(e.target as Node)) {
        setShowTermsModal(false)
      }
    }
    if (showTermsModal) document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [showTermsModal])

  const canSubmit =
    medAck &&
    perAck &&
    terms &&
    !submitting &&
    (medLevel !== 'partial' || medExpl.trim().length > 0) &&
    (perLevel !== 'partial' || perExpl.trim().length > 0)

  const submitForm = async () => {
    setError(null)
    setSubmitting(true)
    try {
      await submitConsentRefresh({
        medical_data_acknowledged: medAck,
        personal_data_acknowledged: perAck,
        medical_consent_level: medLevel,
        medical_consent_explanation: medLevel === 'partial' ? medExpl : '',
        personal_consent_level: perLevel,
        personal_consent_explanation: perLevel === 'partial' ? perExpl : '',
      })
      onDone()
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ??
          (language === 'el' ? 'Σφάλμα υποβολής.' : 'Submission failed.'),
      )
      setSubmitting(false)
    }
  }

  const handleSubmitClick = () => {
    if (!canSubmit) return
    if (medLevel !== 'full') {
      setShowSubmitConfirmDialog(true)
      return
    }
    submitForm()
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">{s.pageTitle}</h1>
          <p className="text-sm text-muted-foreground">{s.pageIntro}</p>
        </div>

        {records.length > 0 && (
          <div className="mb-6 p-4 rounded-lg border bg-muted/30">
            <p className="text-sm font-medium mb-1">{s.childrenLabel}:</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground">
              {records.map((r) => (
                <li key={r.id}>
                  {r.child_name} ({r.year})
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* GDPR Consent Section */}
        <div className="p-4 rounded-lg border-2 border-primary/30 bg-primary/5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">{s.sectionTitle}</h3>
          </div>

          {/* Collapsible GDPR Information Document */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowGdprInfo(!showGdprInfo)}
              className="text-sm text-primary hover:text-primary/80 underline font-medium flex items-center gap-1"
            >
              {showGdprInfo ? s.hideFullInfo : s.readFullInfo}
              {showGdprInfo ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {showGdprInfo && (
              <div className="mt-3 p-4 bg-white rounded-md border border-gray-200 max-h-[400px] overflow-y-auto text-sm text-muted-foreground">
                <h3 className="text-base font-bold mb-3">{s.infoDocumentTitle}</h3>
                <div dangerouslySetInnerHTML={{ __html: s.infoDocumentHtml }} />
              </div>
            )}
          </div>

          {/* Medical Data Consent */}
          <div className="mb-5">
            <h4 className="text-sm font-semibold mb-2">{s.medicalConsentTitle}</h4>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="medical_consent_level"
                  value="full"
                  checked={medLevel === 'full'}
                  onChange={() => {
                    setMedLevel('full')
                    setMedExpl('')
                  }}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{s.fullConsent}</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="medical_consent_level"
                  value="partial"
                  checked={medLevel === 'partial'}
                  onChange={() => {
                    setMedLevel('partial')
                    setShowConsentWarningDialog(true)
                  }}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{s.partialConsent}</span>
              </label>
              {medLevel === 'partial' && (
                <div className="pl-7">
                  <textarea
                    value={medExpl}
                    onChange={(e) => setMedExpl(e.target.value)}
                    placeholder={s.explainPlaceholder}
                    className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    rows={3}
                  />
                </div>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="medical_consent_level"
                  value="none"
                  checked={medLevel === 'none'}
                  onChange={() => {
                    setMedLevel('none')
                    setMedExpl('')
                    setShowConsentWarningDialog(true)
                  }}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{s.noConsent}</span>
              </label>
            </div>
          </div>

          {/* Personal Data Consent */}
          <div className="mb-5">
            <h4 className="text-sm font-semibold mb-2">{s.personalConsentTitle}</h4>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personal_consent_level"
                  value="full"
                  checked={perLevel === 'full'}
                  onChange={() => {
                    setPerLevel('full')
                    setPerExpl('')
                  }}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{s.fullConsent}</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personal_consent_level"
                  value="partial"
                  checked={perLevel === 'partial'}
                  onChange={() => setPerLevel('partial')}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{s.partialConsent}</span>
              </label>
              {perLevel === 'partial' && (
                <div className="pl-7">
                  <textarea
                    value={perExpl}
                    onChange={(e) => setPerExpl(e.target.value)}
                    placeholder={s.explainPlaceholderPersonal}
                    className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    rows={3}
                  />
                </div>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personal_consent_level"
                  value="none"
                  checked={perLevel === 'none'}
                  onChange={() => {
                    setPerLevel('none')
                    setPerExpl('')
                  }}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{s.noConsentPersonal}</span>
              </label>
            </div>
          </div>

          {/* Acknowledgment checkboxes */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={medAck}
                onChange={(e) => setMedAck(e.target.checked)}
                className="h-5 w-5 mt-0.5 rounded border-input focus:ring-ring"
              />
              <span className="text-sm font-medium">{s.medicalDataAcknowledged} *</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={perAck}
                onChange={(e) => setPerAck(e.target.checked)}
                className="h-5 w-5 mt-0.5 rounded border-input focus:ring-ring"
              />
              <span className="text-sm font-medium">{s.personalDataAcknowledged} *</span>
            </label>
          </div>
        </div>

        {/* Terms and Conditions Checkbox */}
        <div className="flex items-start mt-6">
          <div className="flex items-center h-5">
            <input
              id="termsAccepted"
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              className="h-4 w-4 rounded border-input focus:ring-ring"
            />
          </div>
          <div className="ml-3 text-base">
            <label htmlFor="termsAccepted" className="font-medium text-foreground">
              {s.termsLabel}
            </label>
            <p className="text-muted-foreground">
              {s.termsTextPart1}{' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-blue-600 hover:underline focus:outline-none font-medium"
              >
                {s.termsLink}
              </button>{' '}
              {s.termsTextPart2}
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleSubmitClick}
            disabled={!canSubmit}
            className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors shadow h-9 px-6 py-2 ${
              canSubmit
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? s.submitting : s.submit}
          </button>
        </div>
      </div>

      {/* Terms modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50 px-4">
          <div
            ref={termsModalRef}
            className="relative mx-auto p-6 border w-full max-w-2xl shadow-xl rounded-lg bg-white"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl leading-6 font-bold text-foreground">{s.termsModalTitle}</h3>
              <button
                type="button"
                className="text-muted-foreground bg-transparent hover:bg-muted hover:text-foreground rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                onClick={() => setShowTermsModal(false)}
                aria-label={s.close}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-2 text-left text-sm text-muted-foreground max-h-[70vh] overflow-y-auto pr-2">
              <p className="whitespace-pre-line">{s.termsModalContent}</p>
            </div>
          </div>
        </div>
      )}

      {/* Consent Warning Dialog */}
      {showConsentWarningDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50 px-4">
          <div
            ref={consentWarningRef}
            className="relative mx-auto p-6 border w-full max-w-md shadow-xl rounded-lg bg-white"
          >
            <h3 className="text-lg font-bold text-foreground mb-3">{s.warningTitle}</h3>
            <p className="text-sm text-muted-foreground mb-5">{s.warningText}</p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowConsentWarningDialog(false)}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
              >
                {s.ok}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Dialog */}
      {showSubmitConfirmDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50 px-4">
          <div
            ref={submitConfirmRef}
            className="relative mx-auto p-6 border w-full max-w-md shadow-xl rounded-lg bg-white"
          >
            <h3 className="text-lg font-bold text-foreground mb-3">{s.confirmSubmitTitle}</h3>
            <p className="text-sm text-muted-foreground mb-2">{s.warningText}</p>
            <p className="text-sm text-muted-foreground font-medium mb-5">
              {s.confirmSubmitMessage}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowSubmitConfirmDialog(false)}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
              >
                {s.goBack}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSubmitConfirmDialog(false)
                  submitForm()
                }}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
              >
                {s.proceed}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
